import React, { ChangeEvent, FunctionComponent, useState } from 'react';

import { reduce } from 'lodash';
import { Button, Form, Modal, ToggleButton, ToggleButtonGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';

import { ActionId } from 'admin/actions/Actions';
import { Credential, Page, CredentialType, savePasswordCredential } from 'admin/credentials/Credentials';
import { Location } from 'admin/locations/Locations';
import { Source, SourceId } from 'admin/sources/Sources';
import { isAuthorized } from 'auth/Auth';
import { ListBuilder } from 'core/list-builder/ListBuilder';
import { Overlay } from 'core/overlay/Overlay';
import { ButtonVariant, Size } from 'core/util/Bootstrap';

import 'common/credentials/add/AddCredentials.scss';

interface Props {
    agencyId: number;
    tenantId: number;
    location: Location;
    source: Source;
    allowAddCredentials?: boolean;
    onCredentialSave?: (credential: any) => void;
    onCredentialFail?: (error: any) => void;
}

interface FieldPair {
    key: string;
    value: string;
}

export const AddCredentials: FunctionComponent<Props> = ({
    agencyId,
    tenantId,
    location,
    source,
    onCredentialSave,
    onCredentialFail,
    allowAddCredentials = true,
}) => {
    const { t } = useTranslation('common/add-credential');

    const [showModal, setShowModal] = useState(false);

    const [username, setUsername] = useState<string>();
    const [password, setPassword] = useState<string>();
    const [valid, setValid] = useState(true);
    const [verificationFieldPairs, setVerificationFieldPairs] = useState<FieldPair[]>([]);

    const save = async () => {
        if (!username || !password) {
            return;
        }

        const additionalFields = reduce(
            verificationFieldPairs,
            (acc, verificationPair: FieldPair) => {
                return { ...acc, [verificationPair.key]: verificationPair.value };
            },
            {},
        );

        const credential: Partial<Credential> = {
            agencyID: agencyId,
            tenantID: tenantId,
            sourceID: source.id,
            sourceUid: source.uid,
            attached: true,
            credentialType: CredentialType.Password,
            page: {
                locationID: location.id,
                locationUid: location.uid!,
            } as Page,
            valid,
            credentialData: {
                password: {
                    username,
                    password,
                    verificationDetails: Object.keys(additionalFields).length ? additionalFields : undefined,
                },
            },
        };

        try {
            const result = await trackPromise(savePasswordCredential(tenantId, credential), 'add-credentials-form');

            setShowModal(false);
            onCredentialSave?.(result);
        } catch (error) {
            onCredentialFail?.(error);
        }
    };

    return (
        <div className="r4e-add-credentials">
            <Overlay area="add-credentials-root" />
            {isAuthorized({ actions: [ActionId.PlatformCredentials.Add] }) && allowAddCredentials ? (
                <Button variant="link" onClick={() => setShowModal(true)}>
                    {source?.id === SourceId.ApartmentRatings
                        ? t('ADD_CREDENTIALS_TO_RESPOND_APARTMENT_RATINGS')
                        : t('ADD_CREDENTIALS_TO_RESPOND')}
                </Button>
            ) : (
                <span>{t('ADD_CREDENTIALS_TO_RESPOND_ADMINISTRATOR')}</span>
            )}
            {showModal && (
                <Modal show onHide={(): void => setShowModal(false)} className="r4e-add-credentials-modal" centered>
                    <Modal.Header closeButton>
                        <Modal.Title>{t('ADD_CREDENTIALS')}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Overlay area="add-credentials-form" />
                        <Form>
                            <Form.Group>
                                <Form.Label>{t('USERNAME')}</Form.Label>
                                <Form.Control
                                    required
                                    autoFocus
                                    type="text"
                                    value={username}
                                    onChange={({ target }: ChangeEvent<HTMLInputElement>): void =>
                                        setUsername(target.value)
                                    }
                                />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>{t('PASSWORD')}</Form.Label>
                                <Form.Control
                                    required
                                    type="text"
                                    value={password}
                                    onChange={({ target }: ChangeEvent<HTMLInputElement>): void =>
                                        setPassword(target.value)
                                    }
                                />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>{t('STATUS')}</Form.Label>
                                <div className="toggle-button-container">
                                    <ToggleButtonGroup
                                        key="r4e-add-credentials"
                                        type="radio"
                                        name="addCredentials"
                                        defaultValue={valid}
                                        onChange={(valid: boolean) => {
                                            setValid(valid);
                                        }}
                                        size={Size.Small}
                                    >
                                        <ToggleButton value variant={ButtonVariant.Success}>
                                            {t('VALID')}
                                        </ToggleButton>
                                        <ToggleButton value={false} variant={ButtonVariant.Danger}>
                                            {t('INVALID')}
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                </div>
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>{t('ADD_OPTIONAL_VERIFICATION_DETAILS')}</Form.Label>
                                <ListBuilder
                                    {...{
                                        inputMetadata: [
                                            {
                                                id: 'key',
                                                placeholder: t('CREDENTIAL_VERIFICATION_FIELD_NAME'),
                                            },
                                            {
                                                id: 'value',
                                                placeholder: t('CREDENTIAL_VERIFICATION_FIELD_VALUE'),
                                            },
                                        ],
                                        defaultList: verificationFieldPairs,
                                        noDataLabel: t('NO_VERIFICATION_DETAILS'),
                                        onChange: (data: any[]): void => {
                                            setVerificationFieldPairs(data);
                                        },
                                    }}
                                />
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="link" onClick={(): void => setShowModal(false)}>
                            {t('CANCEL')}
                        </Button>
                        <Button variant="primary" onClick={save}>
                            {t('SUBMIT')}
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </div>
    );
};
